/**
 * Shopping module — "Nákupy": the daily-habit hook. Family-wide (not per-child)
 * named lists with a list switcher + new-list flow, an autofocused quick-add
 * input (Enter to add), items grouped by category with an optional assignee chip
 * and a check-off toggle, and a "clear bought" action.
 *
 * Loading/empty/error handling follows the established module pattern
 * (see ContactsModule). Copy comes from `t.shopping.*`; everything is keyed to
 * `family.id` — there is no `activeChild` here.
 */
import { useMemo, useState } from 'react';
import type {
  AddShoppingItemInput,
  Id,
  ShoppingItem,
  ShoppingList,
} from '@solandia/core';
import {
  addShoppingItemInputSchema,
  createShoppingListInputSchema,
  updateShoppingItemInputSchema,
} from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { makePeopleResolver } from '../../family/people';
import { useAsync } from '../../hooks/useAsync';
import {
  Badge,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Form,
  SectionHeader,
  Spinner,
} from '../../components/ui';

export function ShoppingModule() {
  const { t } = useI18n();
  const api = useApi();
  const { family } = useFamily();

  // -- Lists ------------------------------------------------------------------
  const {
    data: lists,
    loading: listsLoading,
    error: listsError,
    reload: reloadLists,
  } = useAsync<ShoppingList[]>(async () => {
    const res = await api.listShoppingLists(family.id);
    return res.items;
  }, [family.id]);

  const [activeListId, setActiveListId] = useState<Id | null>(null);

  // Keep the active list valid as the set of lists changes: fall back to the
  // first list, or null when there are none.
  const activeList = useMemo<ShoppingList | null>(() => {
    if (!lists || lists.length === 0) return null;
    return lists.find((l) => l.id === activeListId) ?? lists[0]!;
  }, [lists, activeListId]);

  const [creatingList, setCreatingList] = useState(false);

  return (
    <section>
      <SectionHeader
        title={t.shopping.title}
        action={
          lists && lists.length > 0 ? (
            <div className="row wrap">
              <label className="sr-only" htmlFor="shopping-list-picker">
                {t.shopping.listLabel}
              </label>
              <select
                id="shopping-list-picker"
                className="select"
                value={activeList?.id ?? ''}
                onChange={(e) => setActiveListId(e.target.value)}
                aria-label={t.shopping.listLabel}
              >
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setCreatingList(true)}
              >
                ＋ {t.shopping.newList}
              </button>
            </div>
          ) : null
        }
      />

      {listsLoading ? <Spinner /> : null}
      {listsError ? <ErrorState message={t.errors.generic} onRetry={reloadLists} /> : null}

      {lists && lists.length === 0 ? (
        <EmptyState
          emoji="🛒"
          title={t.shopping.noLists}
          hint={t.shopping.noListsLead}
          action={
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setCreatingList(true)}
            >
              ＋ {t.shopping.newList}
            </button>
          }
        />
      ) : null}

      {activeList ? <ListView key={activeList.id} list={activeList} /> : null}

      {creatingList ? (
        <NewListDialog
          onClose={() => setCreatingList(false)}
          onCreated={(created) => {
            setCreatingList(false);
            setActiveListId(created.id);
            reloadLists();
          }}
        />
      ) : null}
    </section>
  );
}

/** A grouped item: category label + the items under it. */
interface ItemGroup {
  category: string;
  items: ShoppingItem[];
}

function ListView({ list }: { list: ShoppingList }) {
  const { t } = useI18n();
  const api = useApi();
  const { me } = useFamily();

  const people = useMemo(() => makePeopleResolver(me, t.expenses.you), [me, t.expenses.you]);

  // Members of THIS family, for the assignee picker. `assigneeId` references a
  // member's userId; labels are resolved via the people resolver.
  const members = useMemo(
    () =>
      me.memberships
        .filter((m) => m.family.id === list.familyId && m.membership.status === 'active')
        .map((m) => ({ userId: m.membership.userId, label: people.label(m.membership.userId) })),
    [me.memberships, list.familyId, people],
  );

  const {
    data: items,
    loading,
    error,
    reload,
  } = useAsync<ShoppingItem[]>(async () => {
    const res = await api.listShoppingItems(list.id);
    return res.items;
  }, [list.id]);

  const [quickAdd, setQuickAdd] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ShoppingItem | null>(null);

  // -- Split outstanding (grouped by category) vs. bought ---------------------
  const { groups, bought } = useMemo(() => {
    const all = items ?? [];
    const outstanding = all.filter((i) => !i.checked);
    const boughtItems = all.filter((i) => i.checked);

    const byCategory = new Map<string, ShoppingItem[]>();
    for (const item of outstanding) {
      const key = item.category.trim();
      const arr = byCategory.get(key);
      if (arr) arr.push(item);
      else byCategory.set(key, [item]);
    }
    // Stable order: named categories alphabetically, the uncategorised group last.
    const grouped: ItemGroup[] = [...byCategory.entries()]
      .map(([category, groupItems]) => ({ category, items: groupItems }))
      .sort((a, b) => {
        if (a.category === '') return 1;
        if (b.category === '') return -1;
        return a.category.localeCompare(b.category, 'sk');
      });

    return { groups: grouped, bought: boughtItems };
  }, [items]);

  async function addItem() {
    const name = quickAdd.trim();
    const parsed = addShoppingItemInputSchema.safeParse({ name });
    if (!parsed.success) return;
    setAdding(true);
    try {
      await api.addShoppingItem(list.id, parsed.data as AddShoppingItemInput);
      setQuickAdd('');
      reload();
    } catch {
      /* surface inline on next reload; keep the typed text so it isn't lost */
    } finally {
      setAdding(false);
    }
  }

  async function toggle(item: ShoppingItem) {
    // Optimism kept simple: flip then reload to stay aligned with other modules.
    await api.updateShoppingItem(item.id, { checked: !item.checked });
    reload();
  }

  async function remove(item: ShoppingItem) {
    if (!window.confirm(t.common.confirmDelete)) return;
    await api.deleteShoppingItem(item.id);
    reload();
  }

  async function clearBought() {
    if (!window.confirm(t.shopping.clearBoughtConfirm)) return;
    await api.clearBoughtItems(list.id);
    reload();
  }

  return (
    <div className="stack">
      {/* Quick-add: Enter (form submit) adds; input is autofocused. */}
      <Form onSubmit={addItem}>
        <div className="row">
          <label className="sr-only" htmlFor="shopping-quick-add">
            {t.shopping.itemNameLabel}
          </label>
          <input
            id="shopping-quick-add"
            className="input grow"
            value={quickAdd}
            onChange={(e) => setQuickAdd(e.target.value)}
            placeholder={t.shopping.addItemPlaceholder}
            autoFocus
          />
          <button type="submit" className="btn btn-primary" disabled={adding || quickAdd.trim() === ''}>
            {adding ? t.common.saving : t.shopping.addItem}
          </button>
        </div>
      </Form>

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={t.errors.generic} onRetry={reload} /> : null}

      {items && items.length === 0 ? (
        <EmptyState emoji="🧺" title={t.shopping.empty} hint={t.shopping.emptyLead} />
      ) : null}

      {groups.map((group) => (
        <div key={group.category || '__none__'} className="stack-sm">
          <h3 className="section-subtitle">
            {group.category || t.shopping.categoryNone}
          </h3>
          <ul className="list">
            {group.items.map((item) => (
              <li key={item.id} className="list-item">
                <label className="row grow" style={{ gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={item.checked}
                    onChange={() => toggle(item)}
                    aria-label={`${t.shopping.markBought}: ${item.name}`}
                  />
                  <span className="grow">{item.name}</span>
                  {item.assigneeId ? (
                    <Badge tone="accent">{people.label(item.assigneeId)}</Badge>
                  ) : null}
                </label>
                <div className="row">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setEditing(item)}
                    aria-label={`${t.common.edit} ${item.name}`}
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => remove(item)}
                    aria-label={`${t.common.delete} ${item.name}`}
                  >
                    ✕
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {bought.length > 0 ? (
        <div className="stack-sm">
          <div className="row-between">
            <h3 className="section-subtitle muted">{t.shopping.bought}</h3>
            <button type="button" className="btn btn-ghost btn-sm" onClick={clearBought}>
              {t.shopping.clearBoughtCount.replace('{count}', String(bought.length))}
            </button>
          </div>
          <ul className="list">
            {bought.map((item) => (
              <li key={item.id} className="list-item">
                <label className="row grow" style={{ gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked
                    onChange={() => toggle(item)}
                    aria-label={`${t.shopping.bought}: ${item.name}`}
                  />
                  <span className="grow muted" style={{ textDecoration: 'line-through' }}>
                    {item.name}
                  </span>
                </label>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => remove(item)}
                  aria-label={`${t.common.delete} ${item.name}`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {editing ? (
        <EditItemDialog
          item={editing}
          members={members}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function NewListDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (list: ShoppingList) => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family } = useFamily();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const parsed = createShoppingListInputSchema.safeParse({ name: name.trim() });
    if (!parsed.success) {
      setError(t.common.required);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const created = await api.createShoppingList(family.id, parsed.data.name);
      onCreated(created);
    } catch {
      setError(t.errors.generic);
      setBusy(false);
    }
  }

  return (
    <Dialog title={t.shopping.newListTitle} onClose={onClose} closeLabel={t.common.close}>
      <Form onSubmit={submit}>
        <Field label={t.shopping.listNameLabel} error={error}>
          {({ id, invalid }) => (
            <input
              id={id}
              className="input"
              value={name}
              aria-invalid={invalid}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.shopping.listNamePlaceholder}
              autoFocus
            />
          )}
        </Field>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.common.add}
          </button>
        </div>
      </Form>
    </Dialog>
  );
}

function EditItemDialog({
  item,
  members,
  onClose,
  onSaved,
}: {
  item: ShoppingItem;
  members: Array<{ userId: Id; label: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const [name, setName] = useState(item.name);
  const [category, setCategory] = useState(item.category);
  const [assigneeId, setAssigneeId] = useState<Id | ''>(item.assigneeId ?? '');
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const patch = {
      name: name.trim(),
      category: category.trim(),
      assigneeId: assigneeId === '' ? null : assigneeId,
    };
    const parsed = updateShoppingItemInputSchema.safeParse(patch);
    if (!parsed.success) {
      setError(t.common.required);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await api.updateShoppingItem(item.id, parsed.data);
      onSaved();
    } catch {
      setError(t.errors.generic);
      setBusy(false);
    }
  }

  return (
    <Dialog title={t.shopping.addItem} onClose={onClose} closeLabel={t.common.close}>
      <Form onSubmit={submit}>
        <Field label={t.shopping.itemNameLabel} error={error}>
          {({ id, invalid }) => (
            <input
              id={id}
              className="input"
              value={name}
              aria-invalid={invalid}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          )}
        </Field>
        <Field label={`${t.shopping.categoryLabel} (${t.common.optional})`}>
          {({ id }) => (
            <input
              id={id}
              className="input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder={t.shopping.categoryPlaceholder}
            />
          )}
        </Field>
        <Field label={t.shopping.assigneeLabel}>
          {({ id }) => (
            <select
              id={id}
              className="select"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value as Id | '')}
            >
              <option value="">{t.shopping.assigneeNone}</option>
              {members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.common.save}
          </button>
        </div>
      </Form>
    </Dialog>
  );
}
